import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, ClipboardCheck, Users, Briefcase, Calendar, FileText, IdCard,
    Store, Mail, Activity, BarChart3, Sparkles, ScrollText, LogOut, Menu, X,
    Bell, ShieldAlert, ShieldCheck, LifeBuoy, Shield, Palette, FileCheck, Gavel, CreditCard, UserCheck, Building2, Radio, KeyRound, Trash2, MapPin, Eye, Tag, Newspaper,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import AdminChangePasswordDialog from "@/components/admin/AdminChangePasswordDialog";
import AdminMfaSetupDialog from "@/components/admin/AdminMfaSetupDialog";

const SidebarItem = ({ to, icon: Icon, label, badge, testId, end }) => (
    <NavLink
        to={to}
        end={end}
        data-testid={testId}
        className={({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive
                ? "bg-brand-navy text-white dark:bg-slate-800"
                : "text-foreground/80 hover:bg-accent hover:text-foreground"
            }`
        }
    >
        <Icon size={16} className="shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        {typeof badge === "number" && badge > 0 && (
            <span className="rounded-full bg-brand-green text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {badge > 9 ? "9+" : badge}
            </span>
        )}
    </NavLink>
);

const NAV = [
    {
        section: "Command Center",
        items: [
            { to: "/admin", end: true, icon: LayoutDashboard, label: "Executive Dashboard", testId: "adm-nav-dashboard", roles: ["platform_admin", "recruiter_admin", "support_admin"] },
            { to: "/admin/reviews", icon: ClipboardCheck, label: "Brief Reviews", testId: "adm-nav-reviews", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/users", icon: Users, label: "User Management", testId: "adm-nav-users", roles: ["platform_admin"] },
            { to: "/admin/employer-approvals", icon: UserCheck, label: "Employer Approvals", testId: "adm-nav-employer-approvals", roles: ["platform_admin"] },
            { to: "/admin/employer-orgs", icon: Building2, label: "Employer Orgs", testId: "adm-nav-employer-orgs", roles: ["platform_admin"] },
            { to: "/admin/analytics-hub", icon: BarChart3, label: "Analytics Hub", testId: "adm-nav-analytics-hub", roles: ["platform_admin"] },
            { to: "/admin/notifications", icon: Bell, label: "Notifications", testId: "adm-nav-notifications", roles: ["platform_admin", "recruiter_admin", "support_admin"] },
            { to: "/admin/audit", icon: ScrollText, label: "Audit Log", testId: "adm-nav-audit", roles: ["platform_admin"] },
            { to: "/admin/compliance", icon: FileCheck, label: "Compliance Console", testId: "adm-nav-compliance", roles: ["platform_admin"] },
            { to: "/admin/pay-compliance", icon: Mail, label: "Pay Digest", testId: "adm-nav-pay-digest", roles: ["platform_admin"] },
            { to: "/admin/moderation", icon: ShieldAlert, label: "Messaging Moderation", testId: "adm-nav-moderation", roles: ["platform_admin"] },
            { to: "/admin/skills", icon: Tag, label: "Skills Taxonomy", testId: "adm-nav-skills", roles: ["platform_admin"] },
            { to: "/admin/data-purge", icon: Trash2, label: "Data Purge", testId: "adm-nav-data-purge", roles: ["platform_admin"] },
            { to: "/admin/city-backfill", icon: MapPin, label: "City Backfill", testId: "adm-nav-city-backfill", roles: ["platform_admin"] },
            { to: "/admin/passport-lookup", icon: IdCard, label: "Passport Lookup", testId: "adm-nav-passport-lookup", roles: ["platform_admin"] },
            { to: "/admin/all-professionals", icon: Users, label: "All Professionals", testId: "adm-nav-all-professionals", roles: ["platform_admin"], emailOnly: ["admin@simonaratalent.us"] },
            { to: "/admin/legal", icon: Gavel, label: "Legal Versions", testId: "adm-nav-legal", roles: ["platform_admin"] },
            { to: "/admin/privacy-requests", icon: FileCheck, label: "Privacy Requests", testId: "adm-nav-privacy-requests", roles: ["platform_admin"] },
            { to: "/admin/background-checks", icon: ShieldCheck, label: "Background Checks", testId: "adm-nav-bg-checks", roles: ["platform_admin", "compliance_admin", "support_admin", "recruiter_admin"] },
            { to: "/admin/billing", icon: CreditCard, label: "Billing", testId: "adm-nav-billing", roles: ["platform_admin"] },
            { to: "/admin/pricing-preview", icon: Eye, label: "Pricing Preview", testId: "adm-nav-pricing-preview", roles: ["platform_admin"] },
            { to: "/admin/revenue", icon: Radio, label: "Live Revenue", testId: "adm-nav-revenue", roles: ["platform_admin"] },
            { to: "/admin/attribution", icon: BarChart3, label: "Attribution", testId: "adm-nav-attribution", roles: ["platform_admin"] },
            { to: "/admin/recruit", icon: Sparkles, label: "Simonara Recruit™", testId: "adm-nav-recruit", roles: ["platform_admin"] },
            { to: "/admin/sourcing", icon: UserCheck, label: "Sourcing", testId: "adm-nav-sourcing", roles: ["platform_admin"] },
        ],
    },
    {
        section: "Trust & Support",
        items: [
            { to: "/admin/verifications", icon: ShieldCheck, label: "Verification Center", testId: "adm-nav-verifications", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/verifications/ops", icon: Activity, label: "Verification Ops", testId: "adm-nav-verification-ops", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/employment-screenings", icon: ShieldCheck, label: "Employment Screenings", testId: "adm-nav-employment-screenings", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/personal-background", icon: ShieldCheck, label: "Personal Bkg Checks", testId: "adm-nav-personal-bgc", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/job-moderation", icon: Briefcase, label: "Job Moderation", testId: "adm-nav-job-moderation", roles: ["platform_admin"] },
            { to: "/admin/verification-queue", icon: ClipboardCheck, label: "Verification Pending", testId: "adm-nav-verification-queue", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/fraud", icon: ShieldAlert, label: "Fraud Center", testId: "adm-nav-fraud", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/support", icon: LifeBuoy, label: "Support Center", testId: "adm-nav-support", roles: ["platform_admin", "support_admin"] },
        ],
    },
    {
        section: "Analytics",
        items: [
            { to: "/admin/analytics/talent", icon: Activity, label: "Talent Analytics", testId: "adm-nav-a-talent", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/analytics/employer", icon: BarChart3, label: "Employer Analytics", testId: "adm-nav-a-employer", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/analytics/ai", icon: Sparkles, label: "AI Analytics", testId: "adm-nav-a-ai", roles: ["platform_admin"] },
        ],
    },
    {
        section: "Platform",
        items: [
            { to: "/admin/team", icon: Shield, label: "Team & RBAC", testId: "adm-nav-team", roles: ["platform_admin"] },
            { to: "/admin/content", icon: Palette, label: "Content", testId: "adm-nav-content", roles: ["platform_admin"] },
        ],
    },
    {
        section: "Content & Requests",
        items: [
            { to: "/admin/blog", icon: Newspaper, label: "Blog", testId: "adm-nav-blog", roles: ["platform_admin"] },
            { to: "/admin/jobs", icon: Briefcase, label: "Jobs", testId: "adm-nav-jobs", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/events", icon: Calendar, label: "Events", testId: "adm-nav-events", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/requests", icon: IdCard, label: "Passport Requests", testId: "adm-nav-requests", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/applications", icon: FileText, label: "Applications", testId: "adm-nav-apps", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/resumes", icon: Users, label: "Resumes", testId: "adm-nav-resumes", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/talent", icon: Store, label: "Talent Requests", testId: "adm-nav-talent", roles: ["platform_admin", "recruiter_admin"] },
            { to: "/admin/contact", icon: Mail, label: "Contact", testId: "adm-nav-contact", roles: ["platform_admin", "support_admin"] },
        ],
    },
];

export const AdminLayout = ({ onLogout, children, me }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const logout = () => {
        localStorage.removeItem("simonara_admin_token");
        toast.success("Logged out.");
        if (onLogout) onLogout();
        navigate("/admin");
        setTimeout(() => window.location.reload(), 100);
    };

    const adminRole = me?.admin_role || "platform_admin";
    const adminRoles = React.useMemo(() => {
        const list = Array.isArray(me?.admin_roles) ? me.admin_roles : [];
        return new Set([adminRole, ...list].filter(Boolean));
    }, [me?.admin_roles, adminRole]);
    const adminEmail = (me?.email || "").toLowerCase().trim();
    const [changePwOpen, setChangePwOpen] = useState(false);
    const [mfaOpen, setMfaOpen] = useState(false);
    // Re-fetch /admin/me so the sidebar MFA badge and any downstream checks
    // refresh right after the setup flow completes.
    const refetchMe = async () => {
        try { const { data } = await api.get("/admin/me"); /* let parent AdminShell pick this up */
              window.dispatchEvent(new Event("admin:me-refetch")); }
        catch { /* silent */ }
    };

    // Sidebar badge counts — polled every 60s. Failed fetches silently drop
    // so a temporary API blip never breaks the sidebar.
    const [badges, setBadges] = useState({});
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const pairs = await Promise.allSettled([
                api.get("/admin/verifications/pending-count"),
                api.get("/admin/employer-approvals/count"),
            ]);
            if (cancelled) return;
            const [ver, emp] = pairs;
            setBadges({
                "/admin/verification-queue": ver.status === "fulfilled" ? (ver.value.data?.count || 0) : 0,
                "/admin/employer-approvals": emp.status === "fulfilled" ? (emp.value.data?.count || 0) : 0,
            });
        };
        load();
        const t = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    const nav = useMemo(() =>
        NAV
            .map((sec) => ({
                ...sec,
                items: sec.items
                    .filter((it) => !it.roles || it.roles.some((r) => adminRoles.has(r)) || adminRoles.has("platform_admin"))
                    .filter((it) => !it.emailOnly || it.emailOnly.includes(adminEmail))
                    .map((it) => (badges[it.to] ? { ...it, badge: badges[it.to] } : it)),
            }))
            .filter((sec) => sec.items.length > 0),
        [adminRole, adminRoles, adminEmail, badges]);

    return (
        <div className="min-h-screen bg-brand-sand dark:bg-background flex">
            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky top-0 h-screen z-40 w-64 shrink-0 bg-card border-r border-border overflow-y-auto transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
            >
                <div className="p-4 flex items-center justify-between border-b border-border">
                    <Logo />
                    <button className="lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-3 space-y-6">
                    {nav.map((sec) => (
                        <div key={sec.section}>
                            <div className="px-3 mb-2 text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">
                                {sec.section}
                            </div>
                            <div className="space-y-1">
                                {sec.items.map((it) => <SidebarItem key={it.to} {...it} />)}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="p-3 border-t border-border">
                    {me && (
                        <div className="mb-2 rounded-lg bg-muted/40 border border-border p-2">
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Signed in as</div>
                            <div className="text-xs text-foreground truncate" data-testid="adm-me-email">{me.email}</div>
                            <div className="text-[10px] text-emerald-600 font-mono" data-testid="adm-me-role">{me.admin_role}</div>
                            <div className="mt-1 flex items-center gap-1">
                                {me.mfa_enabled ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
                                        data-testid="adm-mfa-badge-on"
                                        title="Two-factor authentication is enabled"
                                    >
                                        <ShieldCheck size={10} /> MFA on
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-400 border border-amber-500/30"
                                        data-testid="adm-mfa-badge-off"
                                        title="Enable MFA to protect this account"
                                    >
                                        <ShieldAlert size={10} /> MFA off
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => setMfaOpen(true)}
                        className={`w-full text-sm inline-flex items-center justify-center gap-2 mb-2 rounded-md border transition ${me?.mfa_enabled ? "border-border hover:bg-muted/40 text-foreground" : "border-amber-400/50 bg-amber-500/10 text-amber-800 dark:text-amber-300 hover:bg-amber-500/15"} py-2`}
                        data-testid="adm-manage-mfa"
                    >
                        <Shield size={14} /> {me?.mfa_enabled ? "Manage MFA" : "Enable MFA"}
                    </button>
                    <button
                        onClick={() => setChangePwOpen(true)}
                        className="btn-outline w-full text-sm inline-flex items-center justify-center gap-2 mb-2"
                        data-testid="adm-change-password"
                    >
                        <KeyRound size={14} /> Change my password
                    </button>
                    <button onClick={logout} className="btn-outline w-full text-sm inline-flex items-center justify-center gap-2" data-testid="adm-logout">
                        <LogOut size={14} /> Log out
                    </button>
                </div>
            </aside>

            <AdminChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
            <AdminMfaSetupDialog open={mfaOpen} onOpenChange={setMfaOpen} me={me} onDone={refetchMe} />

            {/* Main */}
            <div className="flex-1 min-w-0">
                <div className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border h-14 flex items-center px-4 gap-3 lg:justify-end">
                    <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                        <Menu size={20} />
                    </button>
                    <div className="lg:hidden font-heading font-semibold text-sm">Simonara Ops</div>
                    <div className="ml-auto flex items-center gap-2">
                        <ThemeToggle />
                    </div>
                </div>
                <div className="container-page py-8">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default AdminLayout;
